/**
 * The BluffBusters mark: a Victorian B.
 *
 * THIS FILE IS THE ONLY DEFINITION. The card backs, the chips, the felt, the nav and
 * the favicons all draw from `MARK_PATHS` below. If the letterform is wrong, it is
 * wrong here and nowhere else — which is the point, because a brand mark that exists
 * in six slightly different copies stops being a brand mark.
 *
 * The two static favicon files (`app/web/public/icon.svg`, `site/public/favicon.svg`)
 * cannot import TypeScript, so they carry a copy — and `tests/mark.test.ts` fails if
 * that copy ever stops matching this one.
 *
 * ## The letterform
 *
 * Rye's B: a western Tuscan with spurred terminals and split serifs. Carried as a
 * PATH rather than set as text so it is identical at 20px on a chip and at 512px on
 * an app icon, never waits on a webfont, and cannot be substituted by a fallback
 * face on a device that failed to load one.
 *
 * No suit, anywhere, ever. The deck's faces carry all four because a deck has to be
 * conventional; the brand does not.
 */

/**
 * The mark: the capital B of RYE, the wordmark face named on page 6 of the Frontier
 * Book, taken as its actual glyph outline rather than redrawn by hand.
 *
 * This path was extracted from the shipped font file, so it is not an approximation
 * of the letter on the felt — it is the same letter. An earlier version of this file
 * carried a hand-drawn Tuscan B that was close and therefore worse: a mark that is
 * nearly the wordmark's letter reads as a second, slightly wrong brand.
 *
 * Rye is SIL Open Font Licence, which the Frontier Book records as deliberate: "Zero
 * dollars, legal to embed on the web, print on decks, stitch on a hat."
 */
export const MARK_PATHS = [
  "M25.97 109.42Q21.4 109.42 17.87 107.2Q14.34 109.42 11.32 109.42Q8.31 109.42 6.37 108.9Q4.43 108.38 2.98 107.48Q0 105.68 0 102.98Q0 101.59 0.76 100.55Q2.29 101.73 4.64 101.73Q10.8 101.73 13.16 96.47V62.6Q13.16 59.49 11.84 57.83Q9.76 55.12 4.71 52.7Q9.35 50.55 10.8 48.96Q13.16 46.26 13.16 42.94V13.71Q10.8 8.38 4.64 8.38Q2.29 8.38 0.76 9.56Q0 8.52 0 7.17Q0 5.82 0.8 4.64Q1.59 3.46 2.98 2.63Q6.16 0.69 10.28 0.69Q14.4 0.69 17.87 2.98Q21.47 0.69 25.9 0.69Q30.75 0.69 34.35 3.25Q42.04 0 49.65 0Q57.27 0 63.54 1.49Q69.81 2.98 74.86 5.68Q85.87 11.5 88.57 20.78Q90.1 25.9 95.15 28.39Q89.54 32.27 88.23 37.26Q85.32 48.2 76.45 51.52Q89.47 55.12 93.42 68.56Q94.81 73.34 100 76.39Q94.67 79.36 92.87 85.39Q91.07 91.41 86.53 96.23Q81.99 101.04 75.97 104.16Q64.34 110.11 49.72 110.11Q41 110.11 34.14 106.99Q30.54 109.42 25.97 109.42ZM51.59 48.2Q57.69 48.2 60.25 45.29Q62.95 42.31 62.95 35.04V19.04Q62.95 14.82 58.52 11.91Q54.64 9.35 51.35 9.35Q48.06 9.35 45.95 10.15Q43.84 10.94 42.17 12.33Q38.64 15.24 38.64 19.32V48.2ZM66.9 10.94Q69.25 16.2 69.25 26.32Q69.25 41.2 67.31 45.64Q70.22 45.08 72.23 40.03Q72.99 38.16 73.37 36.08Q73.75 34 74 32.86Q74.24 31.72 74.72 30.89Q75.76 29.16 78.46 28.25Q75.69 27.35 75 26.32Q74.1 25.14 73.55 22.02Q72.99 18.91 72.23 16.93Q71.47 14.96 70.57 13.71Q68.84 11.22 66.9 10.94ZM19.81 89.68Q23.27 88.37 23.61 80.61Q24.03 72.85 24.03 59.14Q24.03 45.43 24 40.89Q23.96 36.36 23.89 33.59Q23.68 25.42 22.33 23.72Q20.98 22.02 19.81 21.47Q19.81 45.57 18.98 48.89Q18.01 51.66 14.27 52.84Q18.42 54.29 18.98 56.37Q19.81 59.7 19.81 80.68ZM38.64 89.96Q38.64 94.81 42.31 97.92Q45.84 100.83 50.76 100.83Q55.54 100.83 58.8 98.75Q65.03 94.67 65.03 89.4V69.11Q65.03 62.81 60.94 59.63Q57.48 56.93 51.87 56.93H38.64ZM69.67 59.07Q71.33 62.81 71.33 76.39Q71.33 93.56 70.08 96.54Q71.54 96.26 73.06 93.01Q74.58 89.75 75.21 86.29Q75.83 82.83 76.07 81.58Q76.32 80.33 76.8 79.36Q77.91 77.29 80.54 76.39Q76.39 75.07 75.66 71.12Q74.93 67.17 74.24 65.03Q73.55 62.88 72.78 61.63Q71.47 59.42 69.67 59.07Z",
] as const;

/**
 * The mark on its own.
 *
 * Colour comes from `currentColor`, so it inherits wherever it is dropped and needs
 * no per-use variant.
 */
export function Mark({ className, title }: { className?: string; title?: string }) {
  return (
    <svg className={className} viewBox="0 0 100 110.11"
         role={title ? "img" : undefined} aria-label={title}
         aria-hidden={title ? undefined : true} focusable="false">
      {MARK_PATHS.map((d, i) => <path key={i} d={d} fill="currentColor" />)}
    </svg>
  );
}

/**
 * A card back: framed ground with the mark centred.
 *
 * `showMark` is false where a card is drawn too small to show the whole letter — a
 * sliced B reads as a smudge, not as a brand — and the frame carries the identity on
 * its own at that size.
 */
export function CardBack({ showMark = true }: { showMark?: boolean }) {
  return (
    <svg className="gcb" viewBox="0 0 100 140" preserveAspectRatio="xMidYMid slice"
         aria-hidden="true" focusable="false">
      <rect className="gcb-ground" width="100" height="140" />
      {/*
        Non-scaling strokes. These rules are drawn in a 100x140 box that renders at
        37px wide on a seat card, so a plain 1.6 stroke came out at half a pixel and
        disappeared — the frame was in the markup and invisible on screen.
      */}
      <rect className="gcb-rule" x="6" y="6" width="88" height="128" rx="5"
            vectorEffect="non-scaling-stroke" />
      <rect className="gcb-rule gcb-rule-thin" x="10.5" y="10.5" width="79" height="119" rx="3"
            vectorEffect="non-scaling-stroke" />
      {showMark && (
        <g className="gcb-ink" transform="translate(26 34) scale(0.48)">
          {MARK_PATHS.map((d, i) => <path key={i} d={d} />)}
        </g>
      )}
    </svg>
  );
}

/**
 * Buster Bucks: the Rye B struck on a brass coin.
 *
 * ==================== THE ONE RULE THIS EXISTS TO ENFORCE ======================
 * Buster Bucks are never written with a dollar sign. Not as a style preference: the
 * prize tournaments are lawful because entry is free, which removes consideration, and
 * a wallet that prints "$5,000" tells the player they are holding money. They are not.
 * The mark is a coin with a letter on it, the word is "Bucks", and `lib/money.ts`
 * refuses to emit a `$` in this format at all.
 * ===============================================================================
 *
 * Drawn from `MARK_PATHS` — the same glyph outline as the card back, the chips, the
 * nav and the favicons, taken from the shipped Rye font rather than redrawn. A brand
 * mark that exists in six slightly different copies stops being a brand mark.
 *
 * The reeded edge is generated rather than hand-placed so the tick count can change
 * without the geometry drifting, which is the same trick the old vector chip used.
 * Colour comes from `app.css` (`.bucks-*`), so the coin is on the one palette and no
 * hex appears in this file.
 */
export function BucksIcon({ className, title, reeds = 36 }: {
  className?: string;
  title?: string;
  reeds?: number;
}) {
  const r = 47, cx = 50, cy = 50;
  const edge = Array.from({ length: reeds }, (_, i) => {
    const a = (i / reeds) * 360;
    return (
      <rect key={i} className="bucks-reed"
            x={cx - 1.1} y={cy - r - 1.6} width={2.2} height={5.2} rx={1}
            transform={`rotate(${a} ${cx} ${cy})`} />
    );
  });
  return (
    <svg className={`bucks${className ? ` ${className}` : ""}`} viewBox="0 0 100 100"
         role={title ? "img" : undefined} aria-label={title}
         aria-hidden={title ? undefined : true} focusable="false">
      <circle className="bucks-rim" cx={cx} cy={cy} r={r} />
      {edge}
      <circle className="bucks-face" cx={cx} cy={cy} r={38} />
      {/* The B, centred and sized so its spurred terminals clear the inner rule. */}
      <g className="bucks-ink" transform="translate(26.5 22.5) scale(0.5)">
        {MARK_PATHS.map((d, i) => <path key={i} d={d} />)}
      </g>
    </svg>
  );
}

/**
 * The chip moved out, and the export stayed.
 *
 * A poker chip carrying the mark used to be drawn here as SVG: a bone disc, six
 * oxblood edge spots, the B in the middle. It was the same drawing for a five and for
 * a five thousand, and colour IS the denomination at a table — so the kit replaced it
 * with the photographed chips from `Poker assets/export/chips/`, which come in seven
 * colours and stack.
 *
 * It is re-exported from here rather than moved, so there is still exactly one `Chip` in
 * the app whichever module a caller reaches for (the classic felt that imported it from
 * here was retired in Phase 9). The mark itself —
 * the thing this file is actually for — is unchanged and still the only definition.
 */
export { Chip, ChipStack, chipColorFor, CHIP_COLORS } from "../art/chips";
